/**
 * Site-specific ImportantDates component for Big Bad Cough Settlement.
 *
 * Extends the common ImportantDates class to handle TBD dates gracefully.
 * When a date is null, empty, or invalid, displays "TBD" (or "Por determinar" in Spanish).
 *
 * @module ImportantDates
 */

import { ImportantDates as BaseImportantDates } from "commonAssets/components/ImportantDates";
import { userLanguage } from "commonAssets/js/constants";

/**
 * Extended ImportantDates class with TBD support.
 * @extends BaseImportantDates
 */
export class ImportantDates extends BaseImportantDates {
    /**
     * Returns the localized "TBD" string based on current user language.
     * @returns {string} "TBD" in English, "Por determinar" in Spanish
     */
    getTBD() {
        return userLanguage === "es" ? "Por determinar" : "TBD";
    }

    /**
     * Checks if a date should be treated as TBD.
     * Handles null, empty, invalid dates, and Unix epoch (CMS default for empty dates).
     * @param {string} params - The date to check.
     * @returns {boolean} True if date should display as TBD.
     */
    isTBD(params) {
        if (!params) return true;
        const date = new Date(params);
        if (isNaN(date.getTime())) return true;
        // CMS UI defaults empty dates to Unix epoch when edited
        if (date.getTime() === 0) return true;
        return false;
    }

    /**
     * Returns a formatted date string, or "TBD" if date is invalid/missing.
     * @param {string} params - The date to be formatted.
     * @returns {string} The formatted date string or "TBD".
     */
    dateString(params) {
        if (this.isTBD(params)) return this.getTBD();
        return super.dateString(params);
    }

    /**
     * Returns a formatted date/time string, or "TBD" if date is invalid/missing.
     * @param {string} params - The date and time string to format.
     * @returns {string} The formatted date/time string or "TBD".
     */
    dateTime(params) {
        if (this.isTBD(params)) return this.getTBD();
        return super.dateTime(params);
    }
}
